'use client';

import { createContext, type ReactNode, useContext, useRef } from 'react';

import {
  createHeaderVisibilityStore,
  type HeaderVisibilityStore,
  initHeaderVisibilityStore,
} from '@stores/header-visibility-store';
import { useStore } from 'zustand';

export type HeaderVisibilityStoreApi = ReturnType<typeof createHeaderVisibilityStore>;

export const HeaderVisibilityStoreContext = createContext<HeaderVisibilityStoreApi | undefined>(
  undefined,
);

export interface HeaderVisibilityStoreProviderProps {
  children: ReactNode;
}

export const HeaderVisibilityStoreProvider = ({ children }: HeaderVisibilityStoreProviderProps) => {
  const storeRef = useRef<HeaderVisibilityStoreApi | null>(null);
  if (!storeRef.current) {
    storeRef.current = createHeaderVisibilityStore(initHeaderVisibilityStore());
  }

  return (
    <HeaderVisibilityStoreContext.Provider value={storeRef.current}>
      {children}
    </HeaderVisibilityStoreContext.Provider>
  );
};

export const useHeaderVisibilityStore = <T,>(selector: (store: HeaderVisibilityStore) => T): T => {
  const headerVisibilityStoreContext = useContext(HeaderVisibilityStoreContext);

  if (!headerVisibilityStoreContext) {
    throw new Error('useHeaderVisibilityStore must be used within HeaderVisibilityStoreProvider');
  }

  return useStore(headerVisibilityStoreContext, selector);
};
